/** The page's `<h1>`, drawn by the page header and not by the top bar - ADR-0212.
 *
 * The shell used to print every screen's name as an `<h1>` in the top bar. The new top bar is for
 * search, help, the bell, the person and the reporting context, and the page header card (`TablePage`,
 * `RecordPage`) carries the name instead, with its icon and small label. A screen has exactly one
 * `<h1>`, so the two cannot both print it: a header that renders `PageTitle` **claims** the heading
 * from the shell, which then draws none, and releases it on unmount. A screen that has not been
 * rebuilt still has the shell's heading, so nothing is without one while the screens move over.
 *
 * Outside a shell (a component test) there is no slot, and this simply renders its own `<h1>`.
 *
 * The claim is made in a layout effect, so the shell's heading is gone before the first paint and the
 * title never shows twice. The ref is the slot's own: focus after a navigation lands on this heading
 * (the shell's F16 rule), which is why it is focusable with `tabIndex={-1}`.
 */

import { createContext, useContext, useLayoutEffect } from "react";
import type { ReactNode, RefObject } from "react";

import styles from "./PageTitle.module.css";

export interface PageTitleSlot {
  /** Where the page's heading element is attached, for focus after a navigation. */
  headingRef: RefObject<HTMLHeadingElement | null>;
  /** Take the heading from the shell; the returned function gives it back. */
  claim: () => () => void;
  /** `<Company> · FY<year>`, drawn small above the title: where this page's figures are for. */
  eyebrow?: string;
}

export const PageTitleContext = createContext<PageTitleSlot | null>(null);

export function PageTitle({ children }: { children: ReactNode }) {
  const slot = useContext(PageTitleContext);
  useLayoutEffect(() => slot?.claim(), [slot]);
  return (
    <div className={styles.block}>
      {slot?.eyebrow ? (
        <span className={styles.eyebrow} data-eyebrow>
          {slot.eyebrow}
        </span>
      ) : null}
      <h1
        ref={slot?.headingRef}
        tabIndex={-1}
        className={styles.title}
        title={typeof children === "string" && children.length > 28 ? children : undefined}
      >
        {children}
      </h1>
    </div>
  );
}
